import { MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { QueryAggregate, type components } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import { GridLayout, MainGrid } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import {
  ALL_CONTACTS_VIEW_ID,
  MY_CONTACTS_VIEW_ID,
  getAllContactsView,
  getMyContactsView,
  useCurrentContactId,
} from './AccountAndContactCustomViews';

// The contacts as cards, grouped by gender and, within each, by age in ranges of five years to begin
// with (`groupLevels`). Cards have no column headers to drag onto the grouping strip: its Add
// grouping menu adds a level — the company, say, which goes outside the ranges, as they can only be
// the innermost — and its chips turn a level's order around, change the range or take the level away.
//
// Each group's header, the width of the list, shows the group's name, how many contacts it holds
// and their average age (`columnAggregates`). Click a header to collapse the group to it.

type QueryGroupBy = components['schemas']['QueryGroupBy'];

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.CardLayout.CardGroupingMainDemo';

// The grouping the cards start with; the strip changes it from there.
const GROUP_LEVELS: readonly QueryGroupBy[] = [
  { columnName: 'gendercode' },
  { columnName: 'ppp_age', rangeSize: 5 },
];

// Under each group's header: how old its contacts are, on average, to one decimal place.
const AGGREGATES = [{ columnName: 'ppp_age', aggregates: [{ aggregate: QueryAggregate.Average, format: 'N1' }] }];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

export function CardGroupingMainSample() {
  const styles = useStyles();
  const t = useT();
  // A contact's id, for views over contacts: an internal user has none, and sees no "my contacts".
  const currentContactId = useCurrentContactId();

  const customViews = useMemo(
    () => [getAllContactsView(true), getMyContactsView(currentContactId)],
    [currentContactId],
  );

  return (
    <div className={styles.root}>
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        defaultLayout={GridLayout.Cards}
        allowChangingLayout
        allowGrouping
        groupLevels={GROUP_LEVELS}
        columnAggregates={AGGREGATES}
        cardImageColumn="entityimage"
        minHeight="600px"
        maxHeight="600px"
        pageSize={20}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        customViewDefinitions={customViews}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
      />
    </div>
  );
}
